/**
 * Canonical passport catalog (BL-075) — the SINGLE source of truth for which
 * stamps and citizen badges exist. platform-api seeds these into the
 * `stamps` / `citizen_badges` DB tables (the catalog both portals display);
 * any other consumer imports from here instead of keeping its own copy.
 *
 * Previously this data was duplicated in platform-api AND brain-service and
 * had drifted; centralizing it here makes drift impossible.
 */
import stampsFile from './stamps.json';
import badgesFile from './badges.json';
import type {
  PassportBadgeDefinition,
  PassportCatalogFile,
  PassportStampDefinition,
} from './types';

export type {
  PassportStampDefinition,
  PassportBadgeDefinition,
  PassportCatalogFile,
} from './types';

// Cast through `unknown` — TS infers the JSON's `category` and condition values
// as plain strings/numbers, which don't overlap the vocabulary unions, so a
// direct assertion is rejected. The catalog spec validates every stamp's
// category and condition keys against the vocabularies instead.
const stampsCatalog = stampsFile as unknown as PassportCatalogFile<{
  stamps: PassportStampDefinition[];
}>;
const badgesCatalog = badgesFile as unknown as PassportCatalogFile<{
  citizenBadges: PassportBadgeDefinition[];
}>;

/** All stamp definitions (active + inactive — consumers filter as needed). */
export const PASSPORT_STAMP_CATALOG: PassportStampDefinition[] = stampsCatalog.content.stamps;

/** All citizen-badge definitions. */
export const PASSPORT_BADGE_CATALOG: PassportBadgeDefinition[] =
  badgesCatalog.content.citizenBadges;

/** The IRR event a QultureUp streak milestone is recorded as. */
export const QULTUREUP_STREAK_MILESTONE_EVENT = 'qultureup_streak_milestone';

/**
 * The streak lengths, in days and ascending, at which a QultureUp streak
 * milestone is recorded: the `streakDays` of every stamp minted on one. A
 * milestone is recorded the first time the citizen's run of days REACHES each
 * length, so every streak stamp has the event it mints on, however the run
 * got there. A new streak stamp in the catalog is a new milestone, with no
 * second list to keep in step.
 */
export const QULTUREUP_STREAK_MILESTONE_DAYS: readonly number[] = [
  ...new Set(
    PASSPORT_STAMP_CATALOG.filter((s) => s.trigger.event === QULTUREUP_STREAK_MILESTONE_EVENT)
      .map((s) => s.trigger.conditions.streakDays)
      .filter((days): days is number => typeof days === 'number')
  ),
].sort((a, b) => a - b);

export const PASSPORT_STAMPS_VERSION = stampsCatalog.version ?? '1.0';
export const PASSPORT_BADGES_VERSION = badgesCatalog.version ?? '1.0';
